import { useEffect, useState } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type {
	AiSuggestionsPlugin,
	AiSuggestionsSharedState,
	Suggestion,
} from '../../aiSuggestionsPluginType';

const selectSelectedSuggestion = (
	state: AiSuggestionsSharedState | undefined,
): Suggestion | undefined => {
	const { selectedSuggestion: selectedId, suggestionById } = state ?? {};
	return selectedId !== undefined && suggestionById !== undefined
		? suggestionById.get(selectedId)
		: undefined;
};

const selectBaseLocalId = (state: AiSuggestionsSharedState | undefined): string | undefined => {
	const { selectedSuggestion: selectedId, baseLocalIdBySuggestionId } = state ?? {};
	return baseLocalIdBySuggestionId && selectedId
		? baseLocalIdBySuggestionId.get(selectedId)
		: undefined;
};

/**
 * Subscribes synchronously (no debounce) to the `aiSuggestions` plugin sharedState and returns
 * the positioning-critical slice — `selectedSuggestion` and `baseLocalId`.
 */
export const useSyncedSelectedSuggestion = (
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>,
): {
	baseLocalId: string | undefined;
	selectedSuggestion: Suggestion | undefined;
} => {
	const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | undefined>(() =>
		selectSelectedSuggestion(api?.aiSuggestions?.sharedState.currentState()),
	);
	const [baseLocalId, setBaseLocalId] = useState<string | undefined>(() =>
		selectBaseLocalId(api?.aiSuggestions?.sharedState.currentState()),
	);

	useEffect(() => {
		const aiSuggestions = api?.aiSuggestions;
		if (!aiSuggestions) {
			return;
		}
		// Re-read once at subscription time so we don't miss a transaction that fired between
		// the initial render and the effect commit.
		const initial = aiSuggestions.sharedState.currentState();
		setSelectedSuggestion(selectSelectedSuggestion(initial));
		setBaseLocalId(selectBaseLocalId(initial));
		return aiSuggestions.sharedState.onChange(({ nextSharedState }) => {
			setSelectedSuggestion(selectSelectedSuggestion(nextSharedState));
			setBaseLocalId(selectBaseLocalId(nextSharedState));
		});
	}, [api]);

	return { selectedSuggestion, baseLocalId };
};
